// @ts-strict-ignore
import { useState, useEffect } from 'react';
import Link from 'next/link';
import Router from 'next/router';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'src/store/store';
import { showModal } from 'src/modules/shared/modal/actions';
import { ModalType } from 'src/constants/modalTypes';
import i18n from 'src/utils/translate';
import { getCommunityLogoUrl } from 'src/utils/linkUtils';
import { useWindowSize } from 'src/utils/hooks/useWindowSize';
import { userHasPermission, userHasRole } from 'src/utils/context';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event, Origin } from 'src/constants/footfall';
import { Permission, Role } from 'src/constants/types';
import Icon from 'src/components/icon';
import Avatar from 'src/components/avatar';
import { text } from 'styles-js/mixins/typography';
import { screenMdMin } from 'styles-js/mediaQueries';
import { NoneButton } from 'styles-js/buttons';
export default function WriteHeader(): JSX.Element {
  const dispatch = useDispatch();
  const {
    isEdited,
    isPoll,
    poll,
    post
  } = useSelector(state => state.pages.write);
  const currentUser = useSelector(({
    context
  }) => context.currentUser);
  const {
    slug,
    logoImageHash,
    name
  } = useSelector(({
    context
  }) => context.currentCommunity);
  const isAdmin = userHasRole(currentUser, [Role.HealthUnlocked, Role.Administrator, Role.Partner]);
  const [isMobile, setIsMobile] = useState(true);
  const screenWidth = useWindowSize().width;
  const showStopEditingPollModal = (url: string, as: string) => {
    dispatch(showModal({
      modalType: ModalType.StopEditingPoll,
      modalProps: {
        onAccept: () => Router.push(url, as)
      }
    }));
  };
  useEffect(() => {
    setIsMobile(screenWidth < screenMdMin);
  }, [screenWidth]);
  return <>
      <HeaderNavbar data-sentry-element="HeaderNavbar" data-sentry-source-file="writeHeader.tsx">
        <HeaderNavbarInner data-sentry-element="HeaderNavbarInner" data-sentry-source-file="writeHeader.tsx">
          <NavItem data-sentry-element="NavItem" data-sentry-source-file="writeHeader.tsx">
            <Link href={`/${slug}`} onClick={() => {
            const origin = isPoll ? poll?.id ? Origin.EditPollPage : Origin.WritePollPage : post?.id ? Origin.EditPostPage : Origin.WritePostPage;
            trackFootfallEvent(Event.Clicked, {
              clickMetadata: {
                origin
              },
              clickType: 'back-to-community'
            });
          }} data-sentry-element="Link" data-sentry-source-file="writeHeader.tsx">

              <Icon icon="open-left" data-sentry-element="Icon" data-sentry-source-file="writeHeader.tsx" />
              {!isMobile ? <Text>{i18n.t('Community home')}</Text> : <MobileEl>
                    <Avatar alt={`${name} image`} url={getCommunityLogoUrl(logoImageHash, true)} />
                    <Text>{name}</Text>
                  </MobileEl>}

            </Link>
          </NavItem>
          {!isMobile && <NavItemCenter>
              <Avatar alt={`${name} image`} url={getCommunityLogoUrl(logoImageHash, true)} />
              <Text>{name}</Text>
            </NavItemCenter>}
          {isAdmin && !isMobile ? <NavItemRight>
              <a href={`/${slug}/settings/dashboard`}>
                <Icon icon="cog" />
                <Text>{i18n.t('Admin')}</Text>
              </a>
            </NavItemRight> : <NavItemRight /> // needed to keep positions of elements
        }
        </HeaderNavbarInner>
      </HeaderNavbar>
      {userHasPermission(currentUser, Permission.CreatePoll) && <SubNavbar data-testid="write-tabs">
          <Link legacyBehavior passHref as={`/${slug}/posts/write`} href={`/community/write?communitySlug=${slug}`}>
            <StyledNoneButton as="a" isActive={!isPoll} onClick={e => {
          if (isPoll && poll?.id && !isEdited) {
            e.preventDefault();
            showStopEditingPollModal(`/community/write?communitySlug=${slug}`, `/${slug}/posts/write`);
          }
        }}>
              {i18n.t('Post')}
            </StyledNoneButton>
          </Link>
          <Link legacyBehavior passHref as={`/${slug}/polls/write`} href={`/community/writePoll?communitySlug=${slug}`}>
            <StyledNoneButton as="a" isActive={isPoll} onClick={e => {
          if (!isPoll && post?.id && !isEdited) {
            e.preventDefault();
            showStopEditingPollModal(`/community/writePoll?communitySlug=${slug}`, `/${slug}/polls/write`);
          }
        }}>
              {i18n.t('Poll')}
            </StyledNoneButton>
          </Link>
        </SubNavbar>}
    </>;
}
const HeaderNavbar = styled.div`
  border-bottom: ${({
  theme
}) => theme.greyLine};
`;
const HeaderNavbarInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  padding: 0 10px;
  min-height: 45px; // grow if title too long on mobile, so we can wrap

  @media (max-width: ${({
  theme
}) => theme.screenXxsMin}) {
    padding: 8px 10px;
  }
`;
const NavItem = styled.div`
  @media (${({
  theme
}) => theme.overScreenMedium}) {
    flex: 1; // admin button not visible for normal users, but want to keep 'comm' center, 'back' left
  }
`;
const NavItemCenter = styled(NavItem)`
  text-align: center;
`;
const NavItemRight = styled(NavItem)`
  text-align: right;
  a {
    ${text};
  }
`;
const Text = styled.span`
  margin-left: 8px;
`;
const MobileEl = styled.span`
  ${text};
  padding-left: 8px;
`;
const SubNavbar = styled.div`
  text-align: center;
  border-bottom: ${({
  theme
}) => theme.greyLine};
`;
const StyledNoneButton = styled(NoneButton)<{
  isActive?: boolean;
}>`
  position: relative;
  min-width: 130px;
  line-height: 34px;
  border: 0;
  &:hover, &:focus, &:active {
    color: ${({
  theme
}) => theme.colorTextBlack};
    ::after {
      background-color: ${({
  theme
}) => theme.colorGreen};
    }
  }
  ::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 6px;
    background-color: ${({
  isActive,
  theme
}) => isActive ? theme.colorGreen : 'transparent'};
  }
`;